<template>
    <section class="section">
        <div class="container">
            <div class="columns">
                <div class="column">
                    <h1 class="title">{{ translate("admin_verification.title") }}</h1>
                </div>
            </div>

            <div class="columns">
                <div class="column is-12">
                    <VerificationSettingsOverview
                        :loading="settingsLoading"
                        :can-change-settings="canManageSettings"
                        @change="openSettingsModal"
                    />
                </div>
            </div>

            <div class="columns">
                <div class="column is-12">
                    <div class="tabs is-boxed">
                        <ul>
                            <li :class="getClassForView('fights')">
                                <a href="#" @click="activateView('fights')">
                                    {{ translate("admin_verification.fights_queue") }}
                                </a>
                            </li>
                            <li :class="getClassForView('full')">
                                <a href="#" @click="activateView('full')">
                                    {{ translate("admin_verification.full_list") }}
                                </a>
                            </li>
                            <li :class="getClassForView('log')">
                                <a href="#" @click="activateView('log')">
                                    {{ translate("admin_verification.history_logs") }}
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <FightsListingVerification
                v-if="activeView === 'fights'"
                :event-id="eventId"
                :event-timezone="eventTimezone"
                :event-types="eventTypes"
            />

            <FullListingVerification
                v-if="activeView === 'full'"
                :event-id="eventId"
                :event-timezone="eventTimezone"
                :event-types="eventTypes"
            />

            <VerificationLog v-if="activeView === 'log'" />

            <VerificationSettingsModal ref="verificationSettingsModal" :is-full-organizer="isFullOrganizer" />
        </div>
    </section>
</template>

<script setup lang="ts">
    import { ref, onMounted } from "vue";
    import { ComponentExposed } from "vue-component-type-helpers";
    import FightsListingVerification from "./FightsListingVerification.vue";
    import FullListingVerification from "./FullListingVerification.vue";
    import VerificationLog from "./VerificationLog.vue";
    import VerificationSettingsModal from "./VerificationSettingsModal.vue";
    import VerificationSettingsOverview from "./VerificationSettingsOverview.vue";
    import history from "../Common/history";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import { useVerificationStore } from "./verificationStore";

    const FIGHTS_VIEW = "fights";

    const historyInstance = history.getInstance(window);

    const props = defineProps<{
        eventId: number;
        eventTimezone: string;
        eventTypes: string[];
        isFullOrganizer: boolean;
        canManageSettings: boolean;
    }>();

    const store = useVerificationStore();
    store.eventId = props.eventId;
    store.eventTimezone = props.eventTimezone;

    const verificationSettingsModal = ref<ComponentExposed<typeof VerificationSettingsModal> | null>(null);

    const activeView = ref(FIGHTS_VIEW);
    const settingsLoading = ref(true);

    onMounted(() => {
        setActiveViewFromLocation();
        // Settings come from the generic settings endpoint, which is not available to limited Staff.
        const fetchSettings = props.canManageSettings ? store.fetchEarlyVerificationsSettings : store.fetchVerificationSettings;
        return fetchSettings()
            .catch(handleError)
            .finally(() => {
                settingsLoading.value = false;
            });
    });

    function activateView(view: string) {
        activeView.value = view;
        updateActiveViewInLocation();
    }

    function getClassForView(view: string) {
        if (view === activeView.value) {
            return "is-active";
        }

        return "";
    }

    function updateActiveViewInLocation() {
        historyInstance.setQueryParam("activeView", activeView.value);
    }

    function setActiveViewFromLocation() {
        activeView.value = historyInstance.getQueryParam("activeView") || FIGHTS_VIEW;
    }

    function openSettingsModal() {
        verificationSettingsModal.value?.open();
    }
</script>

<style scoped>
    .table td {
        vertical-align: middle;
    }
</style>
